import { RefObject, useRef, useState, useEffect } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

export type AnimationType =
  | "fade-in"
  | "fade-in-up"
  | "fade-in-down"
  | "fade-in-up-small"
  | "fade-in-up-large"
  | "fade-in-down-large"
  | "slide-left"
  | "slide-right"
  | "slide-left-large"
  | "slide-right-large"
  | "slide-up-no-opacity"
  | "scale-up"
  | "rotate-in"
  | "clip-horizontal"
  | "height-expand"
  | "clip-circle-reveal";

export interface ScrollAnimationConfig {
  animation?: AnimationType;
  threshold?: number;
  triggerOnce?: boolean;
  delay?: number;
  duration?: number;
  disabled?: boolean;
  disableThreshold?: boolean;
  viewport?: {
    amount?: number;
    margin?: string;
  };
}

export interface ScrollAnimationReturn {
  ref: RefObject<HTMLElement>;
  isInView: boolean;
  className: string;
}

export const useScrollAnimation = ({
  animation = "fade-in-up",
  threshold = 0.2,
  triggerOnce = true,
  delay = 0,
  duration,
  disabled = false,
  disableThreshold = false,
  viewport,
}: ScrollAnimationConfig = {}): ScrollAnimationReturn => {
  const ref = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Disable animations if user prefers reduced motion
  const shouldDisable = disabled || prefersReducedMotion;

  // Build className for CSS animations
  // Add 'in-view' class when element is visible
  let className = `animate-${animation}`;
  if (isInView) {
    className += " in-view";
  }

  // Add delay class if specified
  if (delay && !shouldDisable) {
    if (delay === 100) className += " anim-delay-100";
    else if (delay === 200) className += " anim-delay-200";
    else if (delay === 300) className += " anim-delay-300";
    else if (delay === 500) className += " anim-delay-500";
  }

  // Add duration class if specified
  if (duration && !shouldDisable) {
    if (duration === 400) className += " anim-faster";
    else if (duration === 600) className += " anim-fast";
    else if (duration === 1500) className += " anim-slow";
    else if (duration === 2000) className += " anim-slower";
  }

  // Add IntersectionObserver to track isInView state
  useEffect(() => {
    if (!ref.current || shouldDisable) {
      return;
    }

    const element = ref.current;
    const observerThreshold = viewport?.amount ?? threshold;
    const observerRootMargin = viewport?.margin;

    // Calculate dynamic threshold based on element size and manual override
    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    let dynamicThreshold = observerThreshold;

    // Manual override takes precedence
    if (disableThreshold) {
      dynamicThreshold = 0;
    }
    // Auto-disable threshold for large elements (> 120vh)
    else if (rect.height > viewportHeight * 1.2) {
      dynamicThreshold = 0;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Check if the intersection ratio meets our threshold
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= dynamicThreshold
        ) {
          setIsInView(true);
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce && !entry.isIntersecting) {
          setIsInView(false);
        }
      },
      {
        threshold: dynamicThreshold,
        rootMargin: observerRootMargin,
      },
    );

    // Start observing
    observer.observe(element);

    // Small delay to ensure element is rendered before checking initial visibility
    const checkInitialVisibility = () => {
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const windowWidth =
        window.innerWidth || document.documentElement.clientWidth;

      // Check if element is in viewport
      const isInViewport =
        rect.top < windowHeight &&
        rect.bottom > 0 &&
        rect.left < windowWidth &&
        rect.right > 0;

      // Check if enough of the element is visible based on threshold
      const visibleHeight =
        Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
      const elementHeight = rect.height;
      const visibleRatio =
        elementHeight > 0 ? visibleHeight / elementHeight : 0;

      if (isInViewport && visibleRatio >= dynamicThreshold) {
        setIsInView(true);
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully laid out
    const rafId = requestAnimationFrame(() => {
      checkInitialVisibility();
    });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [
    threshold,
    triggerOnce,
    shouldDisable,
    disableThreshold,
    viewport?.margin,
    viewport?.amount,
  ]);

  if (shouldDisable) {
    return {
      ref,
      isInView: true,
      className: "",
    };
  }

  return {
    ref,
    isInView,
    className,
  };
};
